import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Chip,
  useTheme
} from '@mui/material';
import {
  CloudUpload,
  CloudDownload,
  CreateNewFolder,
  Edit,
  Delete,
  RestoreFromTrash,
  DeleteForever,
  Share,
  LinkOff,
  Visibility,
  Star,
  StarBorder,
  InsertLink,
  MoreVert
} from '@mui/icons-material';
import { useUser } from "../../hooks/Authorize";
import axios from "axios";
import { formatDistanceToNow, parseISO } from 'date-fns';
import { getFileIconAndColor } from '../utils';
// import { useTheme } from "../../style/theme";


function ResourceActivities({ item, mode }) {
  const [activities, setActivities] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const platformUser = JSON.parse(localStorage.getItem("user"));
  const theme = useTheme();
  // const { mode } = useTheme();


  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_NODE_FILE_MANAGER_SERVER}/api/fs/${item._id}/activities`);
        setActivities(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load activities");
        setLoading(false);
      }
    };

    fetchData();
  }, [item._id]);

  const getActionIcon = (actionName) => {
    switch (actionName) {
      case 'Uploaded':
        return <CloudUpload color="primary" />;
      case 'Created folder':
        return <CreateNewFolder color="primary" />;
      case 'Renamed':
        return <Edit color="action" />;
      case 'Moved':
        return <Edit color="action" />;
      case 'Moved to trash':
        return <Delete color="error" />;
      case 'Restored from trash':
        return <RestoreFromTrash color="success" />;
      case 'Permanently deleted':
        return <DeleteForever color="error" />;
      case 'Shared':
        return <Share color="info" />;
      case 'Unshared':
        return <LinkOff color="info" />;
      case 'Permission updated':
        return <Edit color="action" />;
      case 'New version uploaded':
        return <CloudUpload color="primary" />;
      case 'Downloaded':
        return <CloudDownload color="success" />;
      case 'Viewed':
        return <Visibility color="info" />;
      case 'Starred':
        return <Star color="warning" />;
      case 'Unstarred':
        return <StarBorder color="warning" />;
      case 'Shortcut created':
        return <InsertLink color="info" />;
      default:
        return <MoreVert color="action" />;
    }
  };

  const formatTime = (timestamp) => {
    return formatDistanceToNow(parseISO(timestamp), { addSuffix: true });
  };

  const getUserLabel = (userId) => {
    if (userId === platformUser._id) return 'You';
    // In a real app, you'd fetch the actual username here
    return 'Another user';
  };

  const extractResourceName = (activityName) => {
    const matches = activityName.match(/"([^"]*)"/);
    return matches ? matches[1] : null;
  };

  if (loading) {
    return (
      <Box p={3} textAlign="center">
        <Typography
          sx={{
            color: mode === 'dark' ? "rgb(207, 207, 207)" : "rgba(85, 85, 85, 0.8)",

          }}
        >Loading activities...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} textAlign="center" color="error.main">
        <Typography>{error}</Typography>
      </Box>
    );
  }

  if (Object.keys(activities).length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography>No activities found</Typography>
      </Box>
    );
  }

  return (
    <div sx={{
      p: 2,
      borderRadius: 2,
      // backgroundColor: theme.palette.background.paper
      background: mode === 'dark' ? "rgba(31, 31, 31, 1)" : "rgb(255, 255, 255)",

    }}>
      {/* <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Activity</Typography> */}

      {Object.entries(activities).map(([date, items]) => {
        const uniqueMap = new Map();

        // Keep only the latest activity for each (userId + name)
        items.forEach(item => {
          const key = `${item.by}-${item.name}`;
          if (!uniqueMap.has(key) || new Date(item.timestamp) > new Date(uniqueMap.get(key).timestamp)) {
            uniqueMap.set(key, item);
          }
        });

        const uniqueActivities = Array.from(uniqueMap.values());

        return (
          <Box key={date} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{
              fontWeight: 'bold',
              color: mode === 'dark' ? "rgba(207, 207, 207, 0.88)" : "rgba(85, 85, 85, 0.82)",

              mb: 1,
              // color: theme.palette.text.secondary
            }}>
              {date}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List disablePadding>
              {uniqueActivities.map((activity, index) => {
                const resourceName = extractResourceName(activity.name);
                const actionName = resourceName ?
                  activity.name.replace(`"${resourceName}"`, '').trim() :
                  activity.name;

                return (
                  <ListItem
                    key={index}
                    sx={{
                      px: 0,
                      py: 1,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 1
                      }
                    }}
                    secondaryAction={
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(activity.timestamp)}
                      </Typography>
                    }
                  >
                    <ListItemAvatar sx={{ paddingLeft: 1 }}>
                      <Avatar sx={{
                        bgcolor: theme.palette.mode === 'dark' ?
                          theme.palette.grey[800] :
                          theme.palette.grey[200],
                        // color: theme.palette.text.primary,
                        width: 32,
                        height: 32
                      }}>
                        {activity.resource ?
                          getFileIconAndColor({
                            type: activity.resource.type,
                            extension: activity.resource.name.split('.').pop(),
                            isShortcut: false,
                            overrideColor: '',
                            fontSize: 20,
                          }).icon :
                          getActionIcon(actionName)
                        }
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography variant="body1" component="span"
                            sx={{
                              fontSize: "14px",
                              color: mode === 'dark' ? "rgb(207, 207, 207)" : "rgba(85, 85, 85, 0.97)",

                            }}
                          >
                            {actionName}
                          </Typography>
                          {resourceName && (
                            <Chip
                              label={
                                resourceName?.length > 25 ? `${resourceName.substring(0, 25)}...` : resourceName
                              }
                              size="small"
                              sx={{
                                ml: 1,
                                fontSize: "12px",
                                fontWeight: 600,
                                backgroundColor: theme.palette.mode === 'dark' ?
                                  theme.palette.grey[800] :
                                  theme.palette.grey[200]
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
                          {getUserLabel(activity.by)}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        );
      })}

    </div>
  );
}

export default ResourceActivities;